import { ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ApexAxisChartSeries, ApexChart, ApexFill, ApexStroke, ApexDataLabels, ApexTooltip, ApexAnnotations, ApexXAxis, ApexYAxis } from "ngx-apexcharts";
import { MatDialog } from '@angular/material/dialog';
import { ProjectService } from '../shared/project/project.service';
import { CardsModel, IHasStory, Tab, TabAddress, VarianceModel, ViewMode } from '../shared/project/cards/cards-model';
import { Card, CardLocation } from '../shared/project/cards/card';
import { DriverTypeFilter, DriverTypeFilterEditor } from '../shared/project/driver-types/driver-type-filter';
import { DriverType } from '../shared/project/driver-types/driver-type';
import { DataService } from '../shared/network/data.service';
import { NotificationService } from '../shared/notificaton.service';
import { PreaggregationStrategy } from '../shared/preaggregation/preaggregation-strategy';
import { DimensionFilterItem, DimensionsFilter, LogicalOperationType } from '../shared/project/dimensions-filtering/dimensions-filter';
import { DimensionName, ExplodableDimension } from '../shared/project/cards/dimension-name';
import { LegendItem } from '../shared/project/cards/legend-item';
import { DisplayService } from '../shared/display-service';
import { DimensionValue } from '../shared/project/cards/dimension-value';
import { BalanceCard } from '../shared/project/cards/balance-card';
import { DriverSelectionAlgorithm } from '../shared/project/cards/driver-selection-algorithm';
import { DriverTypeWithIndex } from '../shared/project/driver-types/driver-type-with-index';
import { ImportProgressService } from '../shared/import/import-progress.service';
import { DriverTypeSelectionItem } from '../shared/project/driver-types/driver-type-selection-item';
import { RefVolumeBasis } from '../import/column-types/reference/reference-settings';
import { SignalRService } from '../shared/network/signalr.service';
import { CardsLoadingService } from '../shared/project/cards/cards-loading.service';
import { ProcessingProgressDto, InitialProcessingPart }  from '../shared/project/processing-progress-dto';
import { DimensionLockType } from '../shared/project/cards/dimension-lock-type';
import { BenchmarkDiagramPoint, BenchmarkDiagramSeries, BenchmarksModel } from '../shared/benchmarks/benchmarks-model';
import { FancyNumberPipe } from '../shared/directives/fancy-number.pipe';
import { CardHelper, ChartOptions } from './cardHelper';
import { ViewsModel } from '../shared/project/cards/views-model';
import { CardsMultiSelectMode } from './multiselect-state';
import { GUIUpdateOnDimValSelection } from '../shared/project/cards/gui-update-on-dim-val-selection';
import { BaseCardsComponent } from './base-cards-component';
import { LoadingStatus } from '../shared/loading-status';
import { MainFcstWalkModel, FcstWalkTreeNode } from '../shared/project/cards/fcst-walk-model';
import { SettingsModalComponent, SettingsModalData } from './settings-modal.component';
import { EditFilterExpressionModalComponent } from './edit-filter-expression-modal.component';
import { RelatedCardsModalComponent } from './related/related-cards-modal.component';
import { TrendModalComponent } from './trend/trend-modal.component';
import { AggregationSevice } from 'src/app/shared/preaggregation/aggregation-service';
import { UIAggregationSettings } from '../shared/preaggregation/aggregation-settings';
import { UIAggregationMethod } from '../shared/preaggregation/aggregation-method';

@Component({
  templateUrl: './cards.component.html',
  styleUrls: ['./cards.component.scss'],
})
export class CardsComponent extends BaseCardsComponent implements OnInit, OnDestroy {
    DriverType: typeof DriverType = DriverType;   //hack for enum to be available in the template
    public showSideBar = true;
    ViewMode: typeof ViewMode = ViewMode;
    RefVolumeBasis: typeof RefVolumeBasis = RefVolumeBasis;
    DriverSelectionAlgorithm: typeof DriverSelectionAlgorithm = DriverSelectionAlgorithm;
    PreaggregationStrategy: typeof PreaggregationStrategy = PreaggregationStrategy;
    LogicalOperationType: typeof LogicalOperationType = LogicalOperationType;
    DimensionLockType: typeof DimensionLockType = DimensionLockType;
    CardsMultiSelectMode: typeof CardsMultiSelectMode = CardsMultiSelectMode;
    LoadingStatus: typeof LoadingStatus = LoadingStatus;
    private driverTypeFilterEditor: DriverTypeFilterEditor;

    public projectId: string;
    viewsModel: ViewsModel;
    mainFcstWalkModel: MainFcstWalkModel | null;
    cardsModel: CardsModel; //current
    copiedTab: TabAddress | undefined = undefined;
    selectedTabIsCompatible: boolean = false;
    public showFcst: boolean = false;
    public isViewingForecastWalk: boolean = false;
    public selectedGuiTabIndex: number = 0;
    // The latest load of a Trend model (loadBenchmarksModelData) while it is on its way
    private benchmarksModelRequest: { container: CardsModel | Tab } | undefined;

    driverTypeFilter: DriverTypeFilter;
    public toSeqBeforeOutOfScope: number = undefined;
    public toTotBeforeOutOfScope: number = undefined;
    public toSeqAfterOutOfScope: number = undefined;
    public toTotAfterOutOfScope: number = undefined;
    outComeAfterOutOfScope: number;
    legend: LegendItem[][];
    tabDimensionsFilter: DimensionValue[][];
    tabDriverTypeName: string;
    tabDimensionsList: DimensionValue[];
    scopeDriverTypeNames: string[];

    selectedAlgorithm: DriverSelectionAlgorithm;
    minDriverValue: number = 0;
    minDriverValueIsAuto: boolean = false;
    savedAlgorithm: DriverSelectionAlgorithm;
    savedMinDriverValue: number;
    showAlgoSelection: boolean = false;

    loading: boolean = true;
    loadingBackground: boolean = false;
    calcCombosPercent: number = 0;
    graphPercent: number = 0;
    defaultViewCreationPercent: number = 0;
    // Keeps the progress overlay up after the processing until its bars have filled (InitialImportProgressComponent paces them)
    holdProgressOverlay: boolean = false;
    aborted: boolean = false;
    error: string = null;
    retryTimeoutId: any;

    driverTypeFilterIsDirty = false;
    multiSelectMode: CardsMultiSelectMode = CardsMultiSelectMode.None;

    depthFiltersDirty = false;
    showDepthSlider = false;
    allowedMinDepth = 0;
    allowedMaxDepth = 9;
    selectedMinDepth = 0;
    selectedMaxDepth = 9;
    maxPossibleDepth = 9;
    savedMinDepth: number;
    savedMaxDepth: number;

    story: string;
    savedStory: string;
    loadingStory: boolean = false;

    quickViewDeltasCtx = {
        roundTo: 1,
        showPlusForPositive: true,
        cap: undefined,
        scale: undefined,
    };

    public chartOptions: ChartOptions = CardHelper.createChartOptions();

    @ViewChild('dataLegend') dataLegendEl: ElementRef;
    //diagrams in cards
    public pointWidth = 50;
    public viewBoxWidth = 100;
    public viewBoxHeight = 40;

    public xlsxDownloadLink: string = '';
    public dwpDownloadLink: string = '';
    public initialXlsxDownloadLink: string = '';

    public onUpdateStory() {
        if (this.loadingStory) {
            return;
        }
        const cardsCount = this.varianceModel?.cards?.length ?? 0;
        if (!cardsCount) {
            return;
        }
        const count = Math.min(10, cardsCount);
        const indexes = Array.from({ length: count }, (_, i) => i);
        this.loadStory(this.getSelectedTabUId(), indexes);
    }

    constructor(private activatedRoute: ActivatedRoute, private projectService: ProjectService, private router: Router,
        private dataService: DataService, private notifier: NotificationService, private displayService: DisplayService,
        private ngZone: NgZone, private signalRService: SignalRService,
        private cardsLoadingService: CardsLoadingService, public dialog: MatDialog, private aggregationSevice: AggregationSevice) {
            super();
            this.driverTypeFilterEditor = new DriverTypeFilterEditor();
    }

    ngOnInit(): void {
        // Check for guiTab in router state
        const state = history.state;
        if (state && state.guiTab !== undefined) {
            this.selectedGuiTabIndex = state.guiTab;
        }

        this.activatedRoute.queryParams.subscribe((queryParams: any) => {
            let projectId = queryParams.projectId;
            let tabUId = queryParams.tabuid;
            let fcstWalk = queryParams.fcstWalk;
            let highlightBefore = queryParams.highlightBefore ? parseInt(queryParams.highlightBefore, 10) : undefined;
            let highlightAfter = queryParams.highlightAfter ? parseInt(queryParams.highlightAfter, 10) : undefined;
            let guiTab = queryParams.guiTab !== undefined ? parseInt(queryParams.guiTab, 10) : undefined;
            let formCompIx = queryParams.formCompIx !== undefined ? parseInt(queryParams.formCompIx, 10) : undefined;
            if (projectId) {
                if (this.projectId == projectId && fcstWalk !== undefined) {
                    this.router.navigate(['/project-overview'], { queryParams: { projectId: projectId, fcstWalk: fcstWalk } });
                } else if (this.projectId == projectId && tabUId !== undefined) {
                    // An explicit guiTab in the URL is a deep-link and always wins over the
                    // tab's saved gui-tab (applied inside selectTabByUId). Stale guiTab params
                    // can't linger here: tree-view links clear guiTab explicitly.
                    // When the tab is closed (found === false), the closed-tab resolution runs
                    // asynchronously and manages the gui-tab itself, so don't apply guiTab here.
                    const found = this.selectTabByUId(tabUId);
                    if (found) {
                        this.applyGuiTabIndex(guiTab, state);
                        this.applyTrendFormCompIx(formCompIx);
                    }
                    // Apply highlight params after tab selection
                    if (highlightBefore !== undefined && !isNaN(highlightBefore)) {
                        CardHelper.highlightCardsBefore(this.varianceModel?.cards, highlightBefore);
                        this.removeHighlightQueryParams();
                    }
                    if (highlightAfter !== undefined && !isNaN(highlightAfter)) {
                        CardHelper.highlightCardsAfter(this.varianceModel?.cards, highlightAfter, this.varianceModel?.balanceCard);
                        this.removeHighlightQueryParams();
                    }
                } else if (this.projectId == projectId && this.viewsModel && guiTab !== undefined && !isNaN(guiTab)) {
                    // Only the GUI tab changed within the already loaded project (no tab re-selection needed).
                    this.applyGuiTabIndex(guiTab, state);
                } else {
                    this.projectId = projectId;
                    this.signalRService.startConnection();
                    this.signalRService.addInitialProcessingProgressListener(this.createOnProgressUpdateHandler());

                    // Use the CardsLoadingService to load data with async-await
                    this.loadDataAsync(projectId, tabUId, fcstWalk, highlightBefore, highlightAfter, guiTab, formCompIx);
                }
                this.initialXlsxDownloadLink = this.dataService.getBaseUrl() + `/projects/downloadXlsx/${projectId}`
            }
        });
    }

    ngOnDestroy(): void {
        this.signalRService.stopConnection();
    }

    private async loadDataAsync(projectId: string, tabUId: string, fcstWalk: string, highlightBefore?: number, highlightAfter?: number, guiTab?: number, formCompIx?: number): Promise<void> {
        try {
            const res = await this.cardsLoadingService.loadViews(projectId);
            const requestedTabFound = this.loadViews(res, tabUId, highlightBefore, highlightAfter);

            // Only handle navigation history if no specific tabUId was requested
            if (!tabUId) {
                if (history.state.returnViewId) {
                    this.selectTabById(history.state.returnViewId, history.state.returnTabId);
                } else {
                    let savedSelectedViewId = this.getSavedSelectedViewId();
                    if (savedSelectedViewId && this.viewsModel) {
                        let view = this.viewsModel.views.find(v => v.viewId == savedSelectedViewId);
                        if (view) {
                            this.loadModel(view);
                        }
                    }
                }
            }

            // Apply guiTab from query param (takes precedence) or router state if present.
            // Skip when the requested tab is closed: the closed-tab resolution runs
            // asynchronously and manages the gui-tab itself (e.g. Walk under the Trend modal).
            if (tabUId === undefined || requestedTabFound) {
                this.applyGuiTabIndex(guiTab, history.state);
                this.applyTrendFormCompIx(formCompIx);
                // On a (re)load the gui-tab group is created with this gui-tab already selected: it emits no change
                // for it, so onTabChanged doesn't run and the gui-tab (Trend, Related) would never get its data
                this.ensureGuiTabDataLoaded(this.selectedGuiTabIndex);
            }

            if (this.cardsLoadingService.mainFcstWalkModel) {
                this.mainFcstWalkModel = this.cardsLoadingService.mainFcstWalkModel;
                if (fcstWalk !== undefined) {
                    // Forecast walk is handled by ProjectOverviewComponent on its own route
                    this.router.navigate(['/project-overview'], { queryParams: { projectId: projectId, fcstWalk: fcstWalk } });
                }
            }
        } catch (error) {
            console.error('Error loading data', error);
            this.notifier.showError('Error loading data');
        }
    }

    private createXlsxDownLoadLink() {
        if (!this.cardsModel) {
            return;
        }
        let link = `${this.dataService.getBaseUrl()}/Processing/DownloadXlsx?viewId=${this.cardsModel.viewId}`;
        if (this.cardsModel.selectedTabId) {
            link += `&tabId=${this.cardsModel.selectedTabId}`;
        }
        this.xlsxDownloadLink = link;
    }

    private createDwpDownloadLink() {
        if (!this.viewsModel) {
            return;
        }

        this.dwpDownloadLink = this.dataService.getBaseUrl() + `/projects/export/${this.projectId}`;
    }

    private retryCounter: number = 0;
    private initRetry() {
        this.loadingBackground = true;
        this.aborted = false;
        this.error = null;
        this.retryCounter++;
        let self = this;
        this.stopRetries();
        this.retryTimeoutId = setTimeout(()=>{
            console.log('Retrying... Try #' + self.retryCounter);
            self.loadCardsFromServer();
        }, self.getRetryTime());
    }

    private getRetryTime() {
        if (this.retryCounter <= 1) {
            return 1500;
        } else {
            return (6 + this.retryCounter) * 1000;
        }
    }

    private loadCardsFromServer() {
        this.stopRetries();
        this.projectService.getViews(this.projectId).then(res => {
            this.loadViews(res, undefined);
            this.createXlsxDownLoadLink();
            this.createDwpDownloadLink();
        });
    }

    private loadViews(viewsModel: ViewsModel, tabUId: string | undefined, highlightBefore?: number, highlightAfter?: number): boolean {
        if (viewsModel.processingInProgress) {
            if (!this.loadingBackground && !this.holdProgressOverlay && !this.aborted) {
                // Only when the overlay appears: the progress component created with it releases the hold once its bars are full,
                // and one that has already done so won't again (just after the 100%, GetViews can still find the job running)
                this.holdProgressOverlay = true;
            }
            this.loadingBackground = true;
            this.loadProcessingProgress(viewsModel.currentProcessingProgess, this);
            this.initRetry();
            this.loading = false;
            return true;
        } else {
            this.loadingBackground = false;
            this.retryCounter = 0;
            if (this.holdProgressOverlay) {
                // The processing is over, but the polling only got the progress it had reached when last asked
                this.calcCombosPercent = this.graphPercent = this.defaultViewCreationPercent = 100;
            }
        }

        if (!viewsModel.projectName || viewsModel.projectName.trim() === '') {
            console.error('ViewsModel.projectName is empty or null');
        }
        if (!viewsModel.views || viewsModel.views.length === 0) {
            console.error('ViewsModel.views is empty or null');
        }
        if (!viewsModel.currentProcessingProgess && viewsModel.processingInProgress) {
            console.error('ViewsModel.currentProcessingProgess is null/undefined while processingInProgress is true');
        }

        viewsModel.views.sort((a, b) => Number(a.isDefault) - Number(b.isDefault));
        this.viewsModel = viewsModel;
        if (viewsModel.storiesAreBeingLoaded) {
            this.loadStories();
        }
        for (let view of viewsModel.views) {
            this.initModel(view);
        }
        let selectedView: CardsModel;
        if (tabUId !== undefined) {
            let view = this.findViewByTabUId(tabUId);
            if (view) {
                this.selectView(view);
                selectedView = view;
            }
        }
        if (!selectedView) {
            selectedView = this.viewsModel.views.find(v => v.isSelected)
                        || this.viewsModel.views[this.viewsModel.views.length - 1];
        }
        const requestedTabFound = this.loadModel(selectedView, tabUId, highlightBefore, highlightAfter);
        this.createDwpDownloadLink();
        return requestedTabFound;
    }

    private loadModel(model: CardsModel, tabUId: string = undefined, highlightBefore?: number, highlightAfter?: number): boolean {
        let selectedViewIx = this.viewsModel.views.findIndex(v => v.viewId == model.viewId);
        this.viewsModel.views[selectedViewIx] = model;

        // Update the service with the modified views model
        this.cardsLoadingService.updateViewsModel(this.viewsModel);

        this.initModel(model);

        this.cardsModel = model;

        if (!model.forecast) {
            this.showFcst = false;
        }

        let requestedTabFound = true;
        if (tabUId != undefined)  {
            //console.log(`loadModel: selectTabByUId(${tabUId})`);
            requestedTabFound = this.selectTabByUId(tabUId);
        } else {
            if (!model.selectedTabId) {
                //console.log(`loadModel: selectMainTab(this.cardsModel)`, this.cardsModel);
                this.selectMainTab(this.cardsModel);
            } else {
                //console.log(`loadModel: selectMainTab(this.cardsModel)`, this.cardsModel, this.getSelectedTab());
                this.selectTab(this.cardsModel, this.getSelectedTab());
            }
        }

        // Apply highlight params AFTER tab selection so it applies to varianceModel.cards
        if (highlightBefore !== undefined && !isNaN(highlightBefore)) {
            CardHelper.highlightCardsBefore(this.varianceModel?.cards, highlightBefore);
            this.removeHighlightQueryParams();
        }
        if (highlightAfter !== undefined && !isNaN(highlightAfter)) {
            CardHelper.highlightCardsAfter(this.varianceModel?.cards, highlightAfter, this.varianceModel?.balanceCard);
            this.removeHighlightQueryParams();
        }

        const urlTabUId = this.activatedRoute.snapshot.queryParams['tabuid'];
        if (tabUId !== undefined && requestedTabFound) {
            if (urlTabUId != tabUId) {
                //console.log(`loadModel: routeToTabUId(${tabUId})`);
                this.routeToTabUId(tabUId);
            }
        } else if (urlTabUId !== undefined) {
            // No explicit tab was requested (e.g. Undo/Redo changed the selection server-side).
            // Sync the tabuid query param with the actual selection, otherwise the URL keeps a
            // stale uid and clicking that same item in the tree again would not trigger navigation.
            const actualTabUId = this.getSelectedTabUId();
            if (urlTabUId != actualTabUId) {
                if (actualTabUId != undefined) {
                    this.routeToTabUId(actualTabUId);
                } else {
                    this.router.navigate([], {
                        relativeTo: this.activatedRoute,
                        queryParams: { tabuid: null },
                        queryParamsHandling: 'merge',
                        replaceUrl: true
                    });
                }
            }
        }

        this.driverTypeFilterIsDirty = false;
        this.depthFiltersDirty = false;
        this.showDepthSlider = false;
        this.loading = false;
        this.turnOffMultiSelect();
        if (model.warningText) {
            this.notifier.showInfo(model.warningText);
        }
        return requestedTabFound;
    }

    private initModel(model: CardsModel): void {
        //select and init diagram series
        for (let i = 0; i < model.variance.cards.length; ++i) {
            if (model.variance.cards[i].altViewSeq.diagramSeries && model.variance.cards[i].altViewSeq.diagramSeries.length) {
                CardHelper.initCardDiagram(model.variance.cards[i], model.selectedDiagramSeriesName, model.variance.oldControlValue, model.variance.newControlValue, this.seqMode, this.viewBoxWidth, this.viewBoxHeight);
            }
            if (model.forecast && model.forecast.cards[i].altViewSeq.diagramSeries && model.forecast.cards[i].altViewSeq.diagramSeries.length) {
                CardHelper.initCardDiagram(model.forecast.cards[i], model.selectedDiagramSeriesName, model.forecast.oldControlValue, model.forecast.newControlValue, this.seqMode, this.viewBoxWidth, this.viewBoxHeight);
            }
            for (let j = 0; j < model.tabs.length; ++j) {
                for (let i = 0; i < model.tabs[j].variance.cards.length; ++i) {
                    if (model.tabs[j].variance.cards[i].altViewSeq.diagramSeries && model.tabs[j].variance.cards[i].altViewSeq.diagramSeries.length) {
                        CardHelper.initCardDiagram(model.tabs[j].variance.cards[i], model.selectedDiagramSeriesName, model.variance.oldControlValue, model.variance.newControlValue, this.seqMode, this.viewBoxWidth, this.viewBoxHeight);
                    }
                    if (model.tabs[j].forecast && model.tabs[j].forecast.cards[i].altViewSeq.diagramSeries && model.tabs[j].forecast.cards[i].altViewSeq.diagramSeries.length) {
                        CardHelper.initCardDiagram(model.tabs[j].forecast.cards[i], model.selectedDiagramSeriesName, model.forecast.oldControlValue, model.forecast.newControlValue, this.seqMode, this.viewBoxWidth, this.viewBoxHeight);
                    }
                }
            }
        }

        CardHelper.loadBgColorToDimensionsFilter(model.legend, model.dimensionsFilter);
    }

    private loadMainTab(model: CardsModel) {
        this.varianceModel = this.showFcst && model.forecast ? model.forecast : model.variance;
        this.toSeqBeforeOutOfScope = this.toTotBeforeOutOfScope = this.toTotAfterOutOfScope = undefined;
        this.loadWaterfallItems(this.varianceModel.fromSeq, this.getDisplayedOutcomeBeforeOutOfScope(), this.varianceModel.cards, this.varianceModel.balanceCard, this.varianceModel.outOfScopeCards, this.getDisplayedOutcomeAfterOutOfScope());
        this.driverTypeFilter = CardHelper.cloneObject(model.driverTypeFilter);
        this.driverTypeFilterEditor.setManuallySelected(this.driverTypeFilter);
        this.legend = CardHelper.cloneObject(model.legend);
        this.maxPossibleDepth = model.legend.length;
        this.savedMinDepth = this.selectedMinDepth = model.minDriverDepth || 0;
        this.savedMaxDepth = this.selectedMaxDepth = model.maxDriverDepth === 0 ? 0 : (model.maxDriverDepth || this.maxPossibleDepth);
        this.tabDimensionsFilter = null;
        this.selectedAlgorithm = this.savedAlgorithm = model.algorithm;
        this.minDriverValue = this.savedMinDriverValue = model.minDriverValue;
        this.minDriverValueIsAuto = model.minDriverValueIsAuto;
        this.showAlgoSelection = false;
        this.recalcDepthSliderConstraints();
        this.scopeDriverTypeNames = model.scopeDriverTypeNames;
        this.createXlsxDownLoadLink();
        CardHelper.calcCardsFancyNumContexts(this.varianceModel.cards);
        this.calcOutcomeCtxs();
    }

    private selectMainTab(view: CardsModel) {
        this.turnOffMultiSelect();
        this.selectView(view);

        // Clear forecast walk mode and selection
        this.isViewingForecastWalk = false;
        if (this.mainFcstWalkModel) {
            this.mainFcstWalkModel.selectedTabIx = undefined;
            // Update the service with the modified model
            this.cardsLoadingService.updateMainFcstWalkModel(this.mainFcstWalkModel);
        }

        view.selectedTabId = undefined;
        this.selectedTabIsCompatible = view.mainTabIsMarkedCompatible;
        this.driverTypeFilterIsDirty = false;
        this.depthFiltersDirty = false;
        this.showDepthSlider = false;
        this.tabDimensionsList = undefined;
        this.loadMainTab(this.cardsModel);
        this.savedStory = this.story = view.story;
        this.loadingStory = view.loadingStory;
        this.selectedGuiTabIndex = view.selectedGuiTabIndex !== undefined ? view.selectedGuiTabIndex : 0;

        if (this.selectedGuiTabIndex === 3 && !this.getBenchmarksModel()) {
            this.loadBenchmarksModelData();
        }
    }

    private selectTab(view: CardsModel, tab: Tab) {
        this.turnOffMultiSelect();
        this.selectView(view);

        this.showOutOfScope = false;

        // Clear forecast walk mode and selection
        this.isViewingForecastWalk = false;
        if (this.mainFcstWalkModel) {
            this.mainFcstWalkModel.selectedTabIx = undefined;
            // Update the service with the modified model
            this.cardsLoadingService.updateMainFcstWalkModel(this.mainFcstWalkModel);
        }

        view.selectedTabId = tab.id;
        this.selectedTabIsCompatible = tab.isMarkedCompatible;
        this.varianceModel = this.showFcst && tab.forecast ? tab.forecast : tab.variance;

        this.toSeqBeforeOutOfScope = this.varianceModel.toSeqBeforeOutOfScope;
        this.toTotBeforeOutOfScope = this.varianceModel.toTotBeforeOutOfScope;
        this.toSeqAfterOutOfScope = this.varianceModel.toSeqAfterOutOfScope;
        this.toTotAfterOutOfScope = this.varianceModel.toTotAfterOutOfScope;
        this.outComeAfterOutOfScope = tab.outComeAfterOutOfScope;

        this.loadWaterfallItems(this.varianceModel.fromSeq, this.getDisplayedOutcomeBeforeOutOfScope(), this.varianceModel.cards, this.varianceModel.balanceCard, this.varianceModel.outOfScopeCards, this.getDisplayedOutcomeAfterOutOfScope());
        this.driverTypeFilter = this.createDriverTypeFilterWithSingleVal(this.cardsModel.driverTypeFilter, tab.driverType, tab.formulaComponentIndex, tab.availableDriverTypes, tab.selectedViewDriverTypes);

        this.legend = CardHelper.cloneObject(tab.legend);
        this.maxPossibleDepth = tab.legend.length;
        this.savedMinDepth = this.selectedMinDepth = tab.minDriverDepth || 0;
        this.savedMaxDepth = this.selectedMaxDepth = tab.maxDriverDepth === 0 ? 0 : (tab.maxDriverDepth || this.maxPossibleDepth);
        this.tabDimensionsFilter = CardHelper.cloneObject(tab.dimensionValues);
        this.tabDimensionsList = tab.displayDimensionValues;
        this.tabDriverTypeName = tab.driverTypeName;
        this.driverTypeFilterIsDirty = false;
        this.depthFiltersDirty = false;
        this.showDepthSlider = false;
        this.selectedAlgorithm = this.savedAlgorithm = tab.algorithm;
        this.minDriverValue = this.savedMinDriverValue = tab.minDriverValue;
        this.minDriverValueIsAuto = tab.minDriverValueIsAuto;
        this.showAlgoSelection = false;
        this.recalcDepthSliderConstraints();
        this.scopeDriverTypeNames = tab.scopeDriverTypeNames;
        this.createXlsxDownLoadLink();
        CardHelper.calcCardsFancyNumContexts(this.varianceModel.cards);
        if (this.varianceModel.outOfScopeCards) {
            CardHelper.calcCardsFancyNumContexts(this.varianceModel.outOfScopeCards);
        }
        this.calcOutcomeCtxs();
        this.savedStory = this.story = tab.story;
        this.loadingStory = tab.loadingStory;
        this.selectedGuiTabIndex = tab.selectedGuiTabIndex !== undefined ? tab.selectedGuiTabIndex : 0;

        if (this.selectedGuiTabIndex === 4 && this.cardsModel?.selectedTabId && !tab.relatedCardsModel) {
            this.loadRelatedCardsData();
        }
        if (this.selectedGuiTabIndex === 3 && !this.getBenchmarksModel()) {
            this.loadBenchmarksModelData();
        }
    }

    private ensureRecommendedBreakDownLoaded(cardIx: number): void {
        let card = this.varianceModel.cards[cardIx];
        if (!card.isExplodable || !card.explodableBy?.length) {
            return;
        }
        if (card.recommendedBreakdownLoadingStatus > LoadingStatus.NotStarted) {
            return;
        }

        this.dataService.get<number[]>(`processing/findRecommendedBreakdownForCard?viewId=${this.cardsModel.viewId}&tabId=${this.cardsModel.selectedTabId}&cardIx=${cardIx}`)
            .then(res => {
                card.recommendedBreakdownLoadingStatus = LoadingStatus.Done;
                for (let explodableDim of card.explodableBy) {
                    explodableDim.isRecommended = res.includes(explodableDim.index);
                }
            });
    }

    public onDeleteFcstNode(node: FcstWalkTreeNode): void {
        if (!this.mainFcstWalkModel) {
            return;
        }

        const selectedTab = this.isViewingForecastWalk && this.mainFcstWalkModel.selectedTabIx !== undefined
            ? this.mainFcstWalkModel.tabs[this.mainFcstWalkModel.selectedTabIx]
            : undefined;
        const selectionAffected = selectedTab &&
            selectedTab.path.length >= node.tab.path.length &&
            node.tab.path.every((v, i) => v === selectedTab.path[i]);

        const tabIx = this.mainFcstWalkModel.tabs.indexOf(node.tab);
        if (tabIx >= 0) {
            this.mainFcstWalkModel.tabs.splice(tabIx, 1);
            this.mainFcstWalkModel.redoAvailable = false;
            this.cardsLoadingService.updateMainFcstWalkModel(this.mainFcstWalkModel);
            this.dataService.post('processing/CloseFcstWalkTab', {
                projectId: this.projectId,
                tabIx: tabIx,
            });
        }

        if (selectionAffected) {
            const parentPath = node.tab.path.slice(0, -1);
            let fcstWalk: string;
            if (parentPath.length === 0) {
                fcstWalk = 'main';
            } else {
                const parentTabIx = this.mainFcstWalkModel.tabs.findIndex(t =>
                    t.path.length === parentPath.length &&
                    t.path.every((v, i) => v === parentPath[i])
                );
                fcstWalk = String(parentTabIx >= 0 ? parentTabIx : 0);
            }
            this.router.navigate(['/project-overview'], { queryParams: { projectId: this.projectId, fcstWalk: fcstWalk } });
        }
    }

    private routeToTabUId(tabUId: string, guiTabIndex?: number, formCompIx?: number) {
        if (tabUId === undefined || tabUId === null) {
            return;
        }
        const navExtras: any = {
            relativeTo: this.activatedRoute,
            // Put the requested gui-tab in the URL (or clear a stale one), since an
            // explicit guiTab query param always wins over the tab's saved gui-tab.
            // Likewise the formula component for its Trend (applyTrendFormCompIx).
            queryParams: { tabuid: tabUId, guiTab: guiTabIndex !== undefined ? guiTabIndex : null, formCompIx: formCompIx !== undefined ? formCompIx : null },
            queryParamsHandling: 'merge'
        };
        if (guiTabIndex !== undefined) {
            navExtras.state = { guiTab: guiTabIndex };
        }
        this.router.navigate([], navExtras);
    }

    /**
     * Applies the GUI (mat-tab) index from the deep-link query param when present,
     * otherwise falls back to the router state. The query param takes precedence so
     * that shared/bookmarked URLs land on the requested tab.
     */
    private applyGuiTabIndex(guiTab: number | undefined, state: any): void {
        if (guiTab !== undefined && !isNaN(guiTab)) {
            this.selectedGuiTabIndex = guiTab;
        } else if (state && state.guiTab !== undefined) {
            this.selectedGuiTabIndex = state.guiTab;
        }
    }

    /**
     * Applies the formCompIx of a Trend deep-link (the trend links of the stories, a pinned Trend modal): the
     * Trend gui-tab shows that formula component (-1: the outcome) rather than the card's own driver. Call it
     * after the gui-tab is applied. The param is removed from the URL, since navigations that merge query params
     * (the tabs tree's links, picking a gui-tab) would carry it over and apply it again, to another card's Trend too.
     */
    private applyTrendFormCompIx(formCompIx: number | undefined): void {
        if (formCompIx === undefined || isNaN(formCompIx)) {
            return;
        }
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { formCompIx: null },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
        if (this.selectedGuiTabIndex === 3) {
            this.loadBenchmarksModelData(formCompIx);
        }
    }

    private static readonly HIGHLIGHT_QUERY_PARAMS = ['highlightBefore', 'highlightAfter'];

    private removeHighlightQueryParams(): void {
        const currentParams = { ...this.activatedRoute.snapshot.queryParams };
        let hasHighlightParams = false;
        for (const param of CardsComponent.HIGHLIGHT_QUERY_PARAMS) {
            if (currentParams[param]) {
                delete currentParams[param];
                hasHighlightParams = true;
            }
        }
        if (hasHighlightParams) {
            this.router.navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: currentParams,
                replaceUrl: true
            });
            const cardsToUnhighlight = this.varianceModel?.cards;
            setTimeout(() => {
                this.clearCardHighlights(cardsToUnhighlight);
            }, 5000);
        }
    }

    public clearCardHighlights(cards?: Card[]): void {
        const targetCards = cards ?? this.varianceModel?.cards;
        if (targetCards) {
            targetCards.forEach(card => card.isHighlighted = false);
        }
        if (this.varianceModel?.balanceCard) {
            this.varianceModel.balanceCard.isHighlighted = false;
        }
        this.removeHighlightQueryParams();
    }

    /**
     * Selects the view/tab identified by tabUId. If the uid points to a tab that no longer
     * exists (e.g. a stale link in a story to a closed tab), falls back gracefully and
     * removes the stale tabuid from the URL. Returns true if the exact tab was found.
     */
    private selectTabByUId(tabUId: string): boolean {
        let found = true;
        let view = this.viewsModel.views.find(v => v.tabUId === tabUId || (v.tabs && v.tabs.some(t => t.tabUId == tabUId)));
        if (!view) {
            // The linked tab is not open in any view: keep the current selection and try to
            // locate the card the tab was exploded from (cached on cards or via the server).
            found = false;
            if (this.cardsModel.selectedTabId) {
                this.selectTab(this.cardsModel, this.getSelectedTab());
            } else {
                this.selectMainTab(this.cardsModel);
            }
            this.resolveClosedTabUId(tabUId);
        } else if (view.tabUId == tabUId) {
            this.selectMainTab(view);
        } else {
            let tab = view.tabs ? view.tabs.find(t => t.tabUId == tabUId) : undefined;
            if (tab) {
                this.selectTab(view, tab);
            } else {
                this.notifier.showInfo('This link points to a tab that has been closed');
                found = false;
                this.selectMainTab(view);
            }
        }

        if (!found) {
            this.removeTabUIdQueryParam();
        }

        const state = history.state;
        if (state && state.guiTab !== undefined) {
            this.selectedGuiTabIndex = state.guiTab;
        }
        return found;
    }

    /** Removes a stale tabuid from the URL so the broken uid does not persist in history/bookmarks. */
    private removeTabUIdQueryParam(): void {
        const params = this.activatedRoute.snapshot.queryParams;
        if (params['tabuid'] === undefined && params['guiTab'] === undefined && params['formCompIx'] === undefined) {
            return;
        }
        // Also clear guiTab: leaving it would re-trigger the queryParams subscription with
        // the "only guiTab changed" branch and apply the deep-link gui-tab (e.g. Trend) to
        // the fallback tab, while the closed-tab resolution manages the gui-tab itself.
        // The closed-tab resolution has read formCompIx already (for the Trend modal).
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { tabuid: null, guiTab: null, formCompIx: null },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    /**
     * Resolves a tabUId of a closed tab to the card it was exploded from: first via the
     * tabUId cached on cards, then via the server. On success selects the containing tab
     * and, for Trend deep-links (guiTab=3), opens the Trend modal for the card.
     */
    private async resolveClosedTabUId(tabUId: string): Promise<void> {
        const guiTab = parseInt(this.activatedRoute.snapshot.queryParams['guiTab'], 10);
        const formCompIx = parseInt(this.activatedRoute.snapshot.queryParams['formCompIx'], 10);
        //console.log(`resolveClosedTabUId: start tabUId=${tabUId}, guiTab=${guiTab}`);

        let location = this.findCardByCachedTabUId(tabUId);
        //console.log('resolveClosedTabUId: cache lookup result', location);
        if (!location) {
            location = await this.lookupCardLocationOnServer(tabUId);
        }
        if (!location) {
            //console.log('resolveClosedTabUId: no location found, tab is closed');
            this.notifier.showInfo('This link points to a tab that has been closed');
            return;
        }

        const { view, tab, cardIx } = location;
        //console.log(`resolveClosedTabUId: resolved to viewId=${view.viewId}, tabId=${tab?.id ?? '(main tab)'}, cardIx=${cardIx}`);
        const card = (tab ? tab.variance : view.variance)?.cards?.[cardIx];
        if (card) {
            card.tabUId = tabUId;   //cache for future links
        }

        if (tab) {
            this.selectTab(view, tab);
        } else {
            this.selectMainTab(view);
        }

        if (guiTab === 3) { //Trend deep-link: the tab is closed, so show the Trend modal for the card
            //console.log(`resolveClosedTabUId: opening Trend modal for cardIx=${cardIx}`);
            // The Trend is shown in the modal, so the underlying page must NOT switch to its
            // Trend gui-tab. Force the Walk gui-tab underneath and strip guiTab from the URL so
            // it isn't (re)applied to the underlying selection or kept in the bookmark.
            this.selectedGuiTabIndex = 0;
            this.removeGuiTabQueryParam();
            this.showTrendForCard(cardIx, isNaN(formCompIx) ? undefined : formCompIx);
        }
    }

    /** Removes the guiTab query param without touching the current gui-tab selection. */
    private removeGuiTabQueryParam(): void {
        if (this.activatedRoute.snapshot.queryParams['guiTab'] === undefined) {
            return;
        }
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { guiTab: null },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    private findCardByCachedTabUId(tabUId: string): { view: CardsModel, tab: Tab | undefined, cardIx: number } | undefined {
        const findInView = (view: CardsModel): { view: CardsModel, tab: Tab | undefined, cardIx: number } | undefined => {
            let cardIx = view.variance?.cards?.findIndex(c => c.tabUId === tabUId) ?? -1;
            if (cardIx >= 0) {
                return { view, tab: undefined, cardIx };
            }
            for (const tab of view.tabs || []) {
                cardIx = tab.variance?.cards?.findIndex(c => c.tabUId === tabUId) ?? -1;
                if (cardIx >= 0) {
                    return { view, tab, cardIx };
                }
            }
            return undefined;
        };

        // A tab UId names one card of the whole project, so the first view that has it is the one
        for (const view of this.viewsModel.views) {
            const found = findInView(view);
            if (found) {
                return found;
            }
        }
        return undefined;
    }

    /** The server knows the view from the tab UId: story links carry no view id (AIStoriesService.CreateBenchmarksLink). */
    private async lookupCardLocationOnServer(tabUId: string): Promise<{ view: CardsModel, tab: Tab | undefined, cardIx: number } | undefined> {
        console.log(`lookupCardLocationOnServer: input tabUId=${tabUId}`);
        try {
            const res = await this.dataService.get<{ found: boolean, viewId: string | null, cardLocation: CardLocation | null }>(`Processing/CardLocationByTabUId/${this.projectId}?tabUId=${encodeURIComponent(tabUId)}`);
            console.log('lookupCardLocationOnServer: server response', res);
            const loc = res?.found ? res.cardLocation : null;
            if (!loc) {
                console.log('lookupCardLocationOnServer: output undefined (not found on server)');
                return undefined;
            }
            const view = this.viewsModel.views.find(v => v.viewId == res.viewId);
            if (!view) {
                console.log(`lookupCardLocationOnServer: output undefined (view ${res.viewId} not loaded)`);
                return undefined;
            }
            const tab = loc.tabId != null ? view.tabs?.find(t => t.id == String(loc.tabId)) : undefined;
            if (loc.tabId != null && !tab) {
                // The card's containing tab is closed too, so the card is not in the client
                // model at all. The server would need to resolve to an open ancestor instead.
                console.log(`lookupCardLocationOnServer: output undefined (containing tabId ${loc.tabId} is also closed; open tab ids:`, view.tabs?.map(t => t.id), ')');
                return undefined;
            }
            const result = { view, tab, cardIx: loc.cardIndex };
            console.log('lookupCardLocationOnServer: output', result);
            return result;
        } catch (err) {
            console.log('lookupCardLocationOnServer: output undefined (request failed)', err);
            return undefined;
        }
    }

    public selectTabById(viewId: string, tabId: string) {
        let view: CardsModel;
        for (let v of this.viewsModel.views) {
            if (v.viewId == viewId) {
                view = v;
                break;
            }
        }
        if (!view) {
            return;
        }
        if (tabId) {
            for (let t of view.tabs) {
                if (t.id == tabId) {
                    this.selectTab(view, t);
                    break;
                }
            }
        } else {
            this.selectMainTab(view);
        }
    }

    dragStarted(event: any) {
        this.showWaterfall = false;
    }

    async dropped(event: any) {
        this.showWaterfall = true;
        if (event.previousIndex == event.currentIndex) {
            return;
        }
        this.loading = true;
        try {
            let res: any = await this.projectService.reorderCards(this.cardsModel.viewId, this.cardsModel.selectedTabId, event.previousIndex, event.currentIndex);
            this.loadModel(res);
        } finally {
            this.loading = false;
        }
    }

    async toggleLocked(card: Card) {
        this.updateUIWithoutModelReload();
        card.isLocked = !card.isLocked;
        if(!card.isLocked) {
            this.loading = true;
        }
        let cardIndex = this.varianceModel.cards.indexOf(card);
        let res: any = await this.projectService.setCardLock(this.cardsModel.viewId, this.cardsModel.selectedTabId, cardIndex, card.isLocked);
        if(!card.isLocked) {
            try {
                this.loadModel(res);
            } finally {
                this.loading = false;
            }
        }
    }

    public getSelectedTab(): Tab | undefined {
        return this.cardsModel.tabs?.find(t => t.id === this.cardsModel.selectedTabId);
    }

    public getParentTabUId(): string | undefined {
        const selectedTab = this.getSelectedTab();
        if (!selectedTab) {
            return undefined;
        }
        if (!selectedTab?.parentTabId) {
            return this.cardsModel.tabUId;
        }
        const parentTab = this.cardsModel.tabs.find(t => t.id === selectedTab.parentTabId);
        return parentTab?.tabUId ?? this.cardsModel.tabUId;
    }

    /** The page's single SEQ/TOT switch, which the Walk, Summary, Trend and Related (also as modals) show and set */
    public setSeqMode(val: boolean): void {
        this.seqMode = val;
        if (!this.varianceModel) {
            return;
        }
        this.loadWaterfallItems(this.varianceModel.fromSeq, this.getDisplayedOutcomeBeforeOutOfScope(), this.varianceModel.cards, this.varianceModel.balanceCard, this.varianceModel.outOfScopeCards, this.getDisplayedOutcomeAfterOutOfScope());
        for (let card of this.varianceModel.cards) {
            let name = card.selectedDiagram.name;
            card.selectedDiagram = (val ? card.altViewSeq.diagramSeries : card.altViewTot.diagramSeries).find(s => s.name == name);
        }
    }

    setNextDriverTypeFilterViewVal(item: DriverTypeSelectionItem): void {
        this.driverTypeFilterIsDirty = true;
        this.driverTypeFilterEditor.setNextViewVal(this.driverTypeFilter, item);
    }

    selectNextDriverTypeScopeVal(driverType: DriverType, formulaComponentIndex? : number): void {
        this.driverTypeFilterIsDirty = true;
        if (this.cardsModel.selectedTabId) {
            this.driverTypeFilterEditor.selectSingleScope(this.driverTypeFilter, driverType, formulaComponentIndex);
        } else {
            this.driverTypeFilterEditor.setNextScopeVal(this.driverTypeFilter, driverType, formulaComponentIndex);
        }
    }


    selectTabDriverType(driverType: DriverType, formulaComponentIndex? : number): void {
        if (!this.cardsModel.selectedTabId) {
            return;
        }
        let tab = this.cardsModel.tabs.find(t => t.id == this.cardsModel.selectedTabId);
        if (!tab.availableDriverTypes.find(dtwi => dtwi.type == driverType && dtwi.formulaComponentIndex == formulaComponentIndex)) {
            return;
        }
        this.driverTypeFilterIsDirty = true;
        this.driverTypeFilterEditor.selectSingleScope(this.driverTypeFilter, driverType, formulaComponentIndex);
    }

    private createDriverTypeFilterWithSingleVal(filter: DriverTypeFilter, selectedType: DriverType, formulaComponentIndex: number, availableDriverTypes: DriverTypeWithIndex[], selectedViewDriverTypes: DriverTypeWithIndex[]) : DriverTypeFilter {
        let newFilter = CardHelper.cloneObject(filter);
        this.driverTypeFilterEditor.selectSingleScope(newFilter, selectedType, formulaComponentIndex);
        this.driverTypeFilterEditor.selectViewTypes(newFilter, selectedViewDriverTypes);
        let allItems = this.driverTypeFilterEditor.getAllItems(newFilter);
        for (let i = 0; i < allItems.length; ++i) {
            let item = allItems[i];
            if (!availableDriverTypes.find(t => t.type == item.driverTypeWithIndex.type && t.formulaComponentIndex == item.driverTypeWithIndex.formulaComponentIndex)) {
                item.disabled = true;
            }
        }
        return newFilter;
    }

    async applyDriverTypeFilter() {
        if(this.driverTypeFilterEditor.allScopeUnselected(this.driverTypeFilter)) {
            this.notifier.showError('Please select at least one driver type');
            return;
        }

        this.driverTypeFilterIsDirty = false;
        if(this.loading) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.projectService.applyDriverTypeFilter(this.cardsModel.viewId, this.cardsModel.selectedTabId, this.driverTypeFilter);
            this.loadModel(model);
        } finally {
            this.loading = false;
        }
    }

    async cancelDriverTypeFilterChanges() {
        this.driverTypeFilterIsDirty = false;
        if (this.cardsModel.selectedTabId) {
            let tab = this.cardsModel.tabs.find(t => t.id == this.cardsModel.selectedTabId);
            this.driverTypeFilter = this.createDriverTypeFilterWithSingleVal(this.cardsModel.driverTypeFilter, tab.driverType, tab.formulaComponentIndex, tab.availableDriverTypes, tab.selectedViewDriverTypes);
        } else {
            this.driverTypeFilter = CardHelper.cloneObject(this.cardsModel.driverTypeFilter);
        }
    }

    async undo() {
        if (this.loading || (this.cardsModel && !this.cardsModel.undoAvailable)) {
            return;
        }
        try {
            this.loading = true;
            this.unselectTabForCopying();
            let model = await this.projectService.undo(this.cardsModel.viewId);
            this.loadModel(model);
        } finally {
            this.loading = false;
        }
    }

    async redo() {
        if (this.loading || !this.cardsModel.redoAvailable) {
            return;
        }
        try {
            this.loading = true;
            this.unselectTabForCopying();
            var model = await this.projectService.redo(this.cardsModel.viewId);
            this.loadModel(model);
        } finally {
            this.loading = false;
        }
    }

    public setDepthFiltersDirty(val: boolean): void {
        if (!this.depthFiltersDirty && val) {
            this.displayService.scrollToClass(this.ngZone, 'apply-depth-filters-buttons');
        }
        this.depthFiltersDirty = val;
    }

    public setDimLock(dimIndex: number, val: DimensionLockType) {
        this.setDepthFiltersDirty(true);
        switch (val) {
            case DimensionLockType.None:
                for (let i = 0; i < this.legend.length; ++i) {
                    for (let j = 0; j < this.legend[i].length; ++j) {
                        if (this.legend[i][j].index == dimIndex) {
                            this.legend[i][j].lockType = DimensionLockType.None;
                        }
                    }
                }
                break;
            case DimensionLockType.Value:
                for (let i = 0; i < this.legend.length; ++i) {
                    let dimIsInCurHier = false;
                    for (let j = 0; j < this.legend[i].length; ++j) {
                        if (this.legend[i][j].index == dimIndex) {
                            dimIsInCurHier = true;
                            break;
                        }
                    }
                    if (dimIsInCurHier) {
                        for (let j = 0; j < this.legend[i].length; ++j) {
                            if (this.legend[i][j].index == dimIndex) {
                                this.legend[i][j].lockType = DimensionLockType.Value;
                            } else {
                                this.legend[i][j].lockType = DimensionLockType.None;
                            }
                        }
                    }
                }
                break;
            case DimensionLockType.All:
                for (let i = 0; i < this.legend.length; ++i) {
                    let hasSetAValToAllInCurHier = false;
                    for (let j = 0; j < this.legend[i].length; ++j) {
                        if (this.legend[i][j].index == dimIndex) {
                            this.legend[i][j].lockType = DimensionLockType.All;
                            hasSetAValToAllInCurHier = true;
                        } else if (hasSetAValToAllInCurHier) {
                            this.legend[i][j].lockType = DimensionLockType.None;
                        }
                    }
                }
                break;
        }
        this.recalcDepthSliderConstraints();
    }

    cancelDepthFiltersChanges() {
        this.legend = CardHelper.cloneObject(this.getSavedCurrentLegend());
        this.setDepthFiltersDirty(false);
        this.selectedMinDepth = this.savedMinDepth;
        this.selectedMaxDepth = this.savedMaxDepth;
        // Recompute the allowed range from the restored legend and re-clamp the selected
        // depth, so the collapsed badge shows the same values the slider does when opened.
        this.recalcDepthSliderConstraints();
        this.showDepthSlider = false;
    }

    private getSavedCurrentLegend(){
        if (this.cardsModel.selectedTabId) {
            return this.cardsModel.tabs.find(t => t.id == this.cardsModel.selectedTabId).legend;
        } else {
            return this.cardsModel.legend;
        }
    }

    async applyDepthFilters() {
        if (this.selectedMinDepth == this.savedMinDepth
            && this.selectedMaxDepth == this.savedMaxDepth
            && JSON.stringify(this.getSavedCurrentLegend()) === JSON.stringify(this.legend)) {
            this.setDepthFiltersDirty(false);
            this.showDepthSlider = false;
            return;
        }

        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.projectService.applyDepthFilters(this.cardsModel.viewId, this.cardsModel.selectedTabId, this.selectedMinDepth, this.selectedMaxDepth, CardHelper.getLockedDimensions(this.legend));
            this.setDepthFiltersDirty(false);
            this.loadModel(model);
        } finally {
            this.loading = false;
        }
    }

    private recalcDepthSliderConstraints() {
        this.allowedMaxDepth = CardHelper.calcAllowedMaxDepth(this.legend);
        if (this.allowedMaxDepth > this.maxPossibleDepth) {
            this.allowedMaxDepth = this.maxPossibleDepth
        }
        if (this.selectedMaxDepth > this.allowedMaxDepth) {
            this.selectedMaxDepth = this.allowedMaxDepth
        }
        // Every driver in the tab/view is at least as deep as the number of dimensions forced
        // non-{All} (combo-fixed values + value locks), so the slider must not allow a lower min.
        this.allowedMinDepth = CardHelper.calcMinSelectableDepth(this.legend);
        if (this.selectedMinDepth < this.allowedMinDepth) {
            this.selectedMinDepth = this.allowedMinDepth;
        }
        if (this.selectedMaxDepth < this.selectedMinDepth) {
            this.selectedMaxDepth = this.selectedMinDepth;
        }
    }
    
    public toggleDepthSlider(): void {
        this.showDepthSlider = !this.showDepthSlider;
        if (this.showDepthSlider) {
            setTimeout(() => {
                this.dataLegendEl?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, 0);
        }
    }

    public depthSliderValueChanged() {
        if (this.selectedMinDepth != this.savedMinDepth || this.selectedMaxDepth != this.savedMaxDepth) {
            this.setDepthFiltersDirty(true);
        }
    }

    public async explodeCard(i: number, lockedDimIx: number | number[] | null = null, guiTabIndex?: number, formCompIx?: number) {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            const lockedDimIxs = lockedDimIx === null ? [] : (Array.isArray(lockedDimIx) ? lockedDimIx : [lockedDimIx]);
            let model = await this.dataService.post<CardsModel>('Processing/ExplodeCard', {
                viewId: this.cardsModel.viewId,
                cardIndex: i,
                tabId: this.cardsModel.selectedTabId,
                lockedDimIxs: lockedDimIxs,
            });
            this.loadModel(model);
            let selectedTab = model.tabs.find(t => t.id == model.selectedTabId);
            this.routeToTabUId(selectedTab.tabUId, guiTabIndex, formCompIx);
        } finally {
            this.loading = false;
        }
    }

    public hasCheckedBreakdownDims(card: Card): boolean {
        return !!card.explodableBy?.some(d => d.isChecked);
    }

    public toggleBreakdownDim(event: MouseEvent, dim: ExplodableDimension): void {
        event.stopPropagation();
        dim.isChecked = !dim.isChecked;
    }

    public onBreakdownDimClick(event: MouseEvent, cardIx: number, card: Card, dim: ExplodableDimension): void {
        if (this.hasCheckedBreakdownDims(card)) {
            event.stopPropagation();
            dim.isChecked = !dim.isChecked;
        } else {
            this.explodeCard(cardIx, dim.index);
        }
    }

    public explodeCheckedDims(cardIx: number, card: Card): void {
        const lockedDimIxs = (card.explodableBy || []).filter(d => d.isChecked).map(d => d.index);
        if (!lockedDimIxs.length) {
            return;
        }
        this.explodeCard(cardIx, lockedDimIxs);
    }

    public async removeCard(i: number) {
        this.removeCards([i]);
    }

    private async removeCards(cardIndexes: number[]) {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.dataService.post<CardsModel>('Processing/RemoveCards', {
                viewId: this.cardsModel.viewId,
                cardIndexes: cardIndexes,
                tabId: this.cardsModel.selectedTabId,
            });
            this.loadModel(model);
        } finally {
            this.loading = false;
        }
    }

    public addCardToParent(i: number) {
        return this.addCardsToParent([i]);
    }

    private async addCardsToParent(cardIndexes: number[]) {
        if (this.loading) {
            return;
        }
        if (!this.cardsModel.selectedTabId) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.dataService.post<CardsModel>('Processing/AddCardsToParent', {
                viewId: this.cardsModel.viewId,
                cardIndexes: cardIndexes,
                tabId: this.cardsModel.selectedTabId,
            });
            this.loadModel(model);
        } finally {
            this.loading = false;
        }
    }

    public onCloseTab(event: {viewId: string; tabId: string}) {
        this.closeTab(event.viewId, event.tabId);
    }

    public closeTab(viewId: string, tabId: string): void {
        function findTabIdsRecursive(list: string[], allTabs: Tab[], tabId: string) {
            for (let tab of allTabs.filter(t => t.parentTabId === tabId))
            {
                list.push(tab.id);
                findTabIdsRecursive(list, allTabs, tab.id);
            }
        }
        let tabsToClose = [ tabId ];
        findTabIdsRecursive(tabsToClose, this.cardsModel.tabs, tabId);
        if (tabsToClose.indexOf(this.cardsModel.selectedTabId) >= 0) {
            this.selectMainTab(this.cardsModel);
        }
        this.cardsModel.tabs = this.cardsModel.tabs.filter(t => tabsToClose.indexOf(t.id) < 0);

        this.dataService.post<CardsModel>('Processing/CloseTab', {
            viewId: viewId,
            tabId: tabId,
        });
        this.updateUIWithoutModelReload();
    }

    public async explodeBalanceCard() {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.dataService.post<CardsModel>('Processing/ExplodeBalanceCard', {
                viewId: this.cardsModel.viewId,
                tabId: this.cardsModel.selectedTabId,
            });
            this.loadModel(model);
        } finally {
            this.loading = false;
        }
    }

    public algoIconClick(algo: DriverSelectionAlgorithm) {
        if (!this.showAlgoSelection) {
            this.showAlgoSelection = true;
            return;
        }
        this.selectedAlgorithm = algo;
    }

    public async createSubTotal (i: number) {
        if (this.loading || this.varianceModel.cards[i].subtotal || (i >= this.varianceModel.cards.length - 1 && !this.varianceModel.balanceCard)) {
            return;
        }
        const tab = this.getSelectedTab();
        const variance = tab ? tab.variance : this.cardsModel.variance;
        const forecast = tab ? tab.forecast : this.cardsModel.forecast;
        this.setSubtotal(variance, i);
        if (forecast) {
            this.setSubtotal(forecast, i);
        }
        await this.dataService.post<CardsModel>('Processing/CreateSubTotal', {
            viewId: this.cardsModel.viewId,
            tabId: this.cardsModel.selectedTabId,
            cardIndex: i,
        });
        this.updateUIWithoutModelReload();
    }

    private setSubtotal(model: VarianceModel, i: number) {
        model.cards[i].subtotal = {
            name: 'Subtotal',
            isEditing: false,
        };
    }

    private updateUIWithoutModelReload() {
        this.cardsModel.redoAvailable = false;
        this.cardsModel.undoAvailable = true;
    }

    public async renameSubtotal (i: number) {
        this.varianceModel.cards[i].subtotal.isEditing = false;
        this.dataService.post('Processing/RenameSubtotal', {
            viewId: this.cardsModel.viewId,
            tabId: this.cardsModel.selectedTabId,
            cardIndex: i,
            newName: this.varianceModel.cards[i].subtotal.name,
        });
        this.updateUIWithoutModelReload();
    }

    public getSubtotalCumulatedDriverValue (i: number): number {
        let val = 0;
        const cards = this.varianceModel.cards;
        for (let j = i; j >= 0; --j) {
            if (j < i && cards[j].subtotal) {
                break;
            }
            val += cards[j].seqDriverValue;
        }
        return val;
    }

    public getSubtotalValue (i: number): number {
        let val = this.cardsModel.selectedTabId ? this.varianceModel.fromInSeq : this.varianceModel.oldTotalOutcome;
        for (let j = 0; j <= i; ++j) {
            val += this.varianceModel.cards[j].seqDriverValue;
        }
        return val;
    }

    public removeSubtotal (i: number) {
        const tab = this.getSelectedTab();
        const variance = tab ? tab.variance : this.cardsModel.variance;
        const forecast = tab ? tab.forecast : this.cardsModel.forecast;
        variance.cards[i].subtotal = undefined;
        if (forecast) {
            forecast.cards[i].subtotal = undefined;
        }
        this.updateUIWithoutModelReload();
        this.dataService.post('Processing/RemoveSubtotal', {
            viewId: this.cardsModel.viewId,
            tabId: this.cardsModel.selectedTabId,
            cardIndex: i,
        });
    }

    public toggleShowOutOfScope(show: boolean) {
        this.showOutOfScope = show;
        this.loadWaterfallItems(this.varianceModel.fromSeq, this.getDisplayedOutcomeBeforeOutOfScope(), this.varianceModel.cards, this.varianceModel.balanceCard, this.varianceModel.outOfScopeCards, this.getDisplayedOutcomeAfterOutOfScope());
    }

    private enableAbortedState(self: CardsComponent, error: string) {
        self.aborted = true;
        self.error = error;
        self.loadingBackground = self.holdProgressOverlay = false;
        self.stopRetries();
    }

    private stopRetries() {
        clearTimeout(this.retryTimeoutId);
    }

    public async abort() {
        if (!this.loadingBackground) {
            // The overlay is only held until its bars have filled: the processing is done and must not be shown as aborted
            return;
        }
        try {
            this.stopRetries();
            await this.projectService.abortProcessing(this.projectId);
            this.enableAbortedState(this, null);
        } finally {
            this.loading = this.loadingBackground = this.holdProgressOverlay = false;
        }
    }

    public reloadAllViews(loadingBackground: boolean) {
        this.loadingBackground = loadingBackground;
        this.aborted = false;
        this.retryCounter = 0;
        this.calcCombosPercent = 0;
        this.graphPercent = 0;
        this.defaultViewCreationPercent = 0;
        this.loadCardsFromServer();
    }

    public async reloadCurrentView(selectParent: boolean = false) {
        if (!this.cardsModel || !this.cardsModel.viewId) {
            console.error('No current view to reload');
            return;
        }

        const currentViewId = this.cardsModel.viewId;
        let targetTabUId: string | undefined;
        let parentTabId: string | null | undefined;

        if (selectParent) {
            const currentTab = this.getSelectedTab();
            if (!currentTab) {
                targetTabUId = this.getCurrentTabUId();
            } else {
                parentTabId = currentTab.parentTabId;
            }
        } else {
            targetTabUId = this.getCurrentTabUId();
        }

        try {
            this.loading = true;
            const viewsModel = await this.projectService.getViews(this.projectId);

            const updatedView = viewsModel.views.find(v => v.viewId === currentViewId);

            if (!updatedView) {
                this.notifier.showError('Current view not found after reload');
                return;
            }

            const hadForecastAdjustmentsBefore = this.viewsModel.haveForecastAdjustmentsDone;
            this.viewsModel.haveForecastAdjustmentsDone = viewsModel.haveForecastAdjustmentsDone;
            if (!hadForecastAdjustmentsBefore && viewsModel.haveForecastAdjustmentsDone) {
                await this.cardsLoadingService.loadMainFcstWalkModel(this.projectId);
                this.mainFcstWalkModel = this.cardsLoadingService.mainFcstWalkModel;
            }

            if (selectParent && targetTabUId === undefined) {
                if (parentTabId === null) {
                    targetTabUId = updatedView.tabUId;
                } else if (parentTabId !== undefined) {
                    const parentTab = updatedView.tabs?.find(t => t.id === parentTabId);
                    targetTabUId = parentTab ? parentTab.tabUId : updatedView.tabUId;
                }

                this.selectedGuiTabIndex = 0;  //select mat-tab Walk
            }

            let oldCards: Card[] = [];
            if (selectParent && targetTabUId) {
                const targetView = this.viewsModel.views.find(v => v.tabUId === targetTabUId || (v.tabs && v.tabs.some(t => t.tabUId === targetTabUId)));
                if (targetView) {
                    if (targetView.tabUId === targetTabUId) {
                        oldCards = targetView.variance?.cards || [];
                    } else {
                        const targetTab = targetView.tabs?.find(t => t.tabUId === targetTabUId);
                        oldCards = targetTab?.variance?.cards || [];
                    }
                }
            } else {
                oldCards = this.varianceModel?.cards || [];
            }

            const viewIndex = this.viewsModel.views.findIndex(v => v.viewId === currentViewId);
            if (viewIndex >= 0) {
                this.viewsModel.views[viewIndex] = updatedView;
            }

            //console.log('oldCards:', oldCards?.map(c => CardHelper.createLogCard(c)));
            //console.log('updatedView.variance?.cards:', updatedView.variance?.cards?.map(c => CardHelper.createLogCard(c)));

            let updatedCards: Card[] = [];
            if (targetTabUId) {
                if (updatedView.tabUId === targetTabUId) {
                    updatedCards = updatedView.variance?.cards || [];
                } else {
                    const updatedTab = updatedView.tabs?.find(t => t.tabUId === targetTabUId);
                    updatedCards = updatedTab?.variance?.cards || [];
                }
            } else if (updatedView.selectedTabId) {
                const selectedTab = updatedView.tabs?.find(t => t.id === updatedView.selectedTabId);
                updatedCards = selectedTab?.variance?.cards || [];
            } else {
                updatedCards = updatedView.variance?.cards || [];
            }

            CardHelper.highlightNewlyAddedCards(oldCards, updatedCards);

            this.loadModel(updatedView, targetTabUId);

        } finally {
            this.loading = false;
        }
    }

    private createOnProgressUpdateHandler() {
        let self = this;
        return function(dto: ProcessingProgressDto) {
            if (!self.loadingBackground) {
                console.warn('Hit createOnProgressUpdateHandler without loadingBackground');
                console.log(dto);
                return;
            }
            if (dto.aborted) {
                console.log('aborted');
                self.enableAbortedState(self, dto.error);
            }
            self.loadProcessingProgress(dto, self);
            // Check if all parts are complete
            if (self.calcCombosPercent >= 100 && self.graphPercent >= 100 && self.defaultViewCreationPercent >= 100) {
                self.loadingBackground = false;
                self.loading = true;
                self.loadCardsFromServer();
            }
        }
    }

    private loadProcessingProgress(dto: ProcessingProgressDto, self: CardsComponent) {
        if (!dto || dto.projectId != self.projectId) {
            return;
        }

        // Update the appropriate progress percentage based on the part
        if (dto.partPercent !== null && dto.partPercent !== undefined) {
            const percent = Math.round(dto.partPercent);

            switch (dto.part) {
                case InitialProcessingPart.CalcCombos:
                    if (percent > self.calcCombosPercent) {
                        self.calcCombosPercent = percent;
                    }
                    break;
                case InitialProcessingPart.Graph:
                    // If Graph is in progress, CalcCombos must be complete
                    self.calcCombosPercent = 100;
                    if (percent > self.graphPercent) {
                        self.graphPercent = percent;
                    }
                    break;
                case InitialProcessingPart.DefaultViewCreation:
                    // If DefaultViewCreation is in progress, all previous parts must be complete
                    self.calcCombosPercent = 100;
                    self.graphPercent = 100;
                    if (percent > self.defaultViewCreationPercent) {
                        self.defaultViewCreationPercent = percent;
                    }
                    break;
            }
        }
    }

    public onMenuOpened(i) {
        this.varianceModel.cards[i].menuOpened = true;
        this.ensureRecommendedBreakDownLoaded(i);
    }

    public onMenuClosed(card: Card) {
        card.menuOpened = false;
        card.explodableBy?.forEach(d => d.isChecked = false);
    }

    public selectSeries(ser: BenchmarkDiagramSeries) {
        for (let i = 0; i < this.varianceModel.cards.length; ++i) {
            let card = this.varianceModel.cards[i];
            card.selectedDiagram = (this.seqMode ? card.altViewSeq.diagramSeries : card.altViewTot.diagramSeries).find(d => d.name == ser.name);
        }
        if (this.cardsModel.selectedDiagramSeriesName === ser.name) {
            return;
        }
        this.cardsModel.selectedDiagramSeriesName = ser.name;
        this.updateUIWithoutModelReload();
        this.dataService.post<CardsModel>('Processing/SelectSeries', {
            viewId: this.cardsModel.viewId,
            seriesName: ser.name,
        });
    }

    public async removeDimensionFromCard(cardIx: number, dimIx: number) {
        try {
            this.loading = true;
            let cardsModel = await this.dataService.post<CardsModel>('Processing/RemoveDimensionFromCard', {
                viewId: this.cardsModel.viewId,
                tabId: this.cardsModel.selectedTabId,
                cardIndex: cardIx,
                dimIndex: dimIx,
            });
            this.loadModel(cardsModel);
        } finally {
            this.loading = false;
        }
    }

    public async addSingleDimensionCard(cardIx: number, dimIx: number) {
        try {
            this.loading = true;
            let cardsModel = await this.dataService.post<CardsModel>('Processing/AddSingleDimensionCard', {
                viewId: this.cardsModel.viewId,
                tabId: this.cardsModel.selectedTabId,
                cardIndex: cardIx,
                dimIndex: dimIx,
            });
            this.loadModel(cardsModel);
        } finally {
            this.loading = false;
        }
    }

    public async addNoDimensionsCard(cardIx: number) {
        try {
            this.loading = true;
            let cardsModel = await this.dataService.post<CardsModel>('Processing/AddNoDimensionsCard', {
                viewId: this.cardsModel.viewId,
                tabId: this.cardsModel.selectedTabId,
                cardIndex: cardIx,
            });
            this.loadModel(cardsModel);
        } finally {
            this.loading = false;
        }
    }

    public async replaceCardWithOutOfScopeCard(cardIx: number) {
        try {
            this.loading = true;
            let cardsModel = await this.dataService.post<CardsModel>('Processing/ReplaceCardWithOutOfScopeCard', {
                viewId: this.cardsModel.viewId,
                tabId: this.cardsModel.selectedTabId,
                cardIndex: cardIx,
            });
            this.loadModel(cardsModel);
        } finally {
            this.loading = false;
        }
    }

    public async addCardAfterTab(cardIx: number) {
        try {
            this.loading = true;
            let cardsModel = await this.dataService.post<CardsModel>('Processing/AddCardAfterTab', {
                viewId: this.cardsModel.viewId,
                tabId: this.cardsModel.selectedTabId,
                cardIndex: cardIx,
            });
            this.loadModel(cardsModel);
        } finally {
            this.loading = false;
        }
    }

    public selectView(view: CardsModel) {
        for (let view of this.viewsModel.views) {
            view.isSelected = false;
            view.mode = ViewMode.normal;
        }
        view.isSelected = true;
        this.cardsModel = view;
    }

    private findViewByTabUId(tabUId: string): CardsModel {
        for (let i = 0; i < this.viewsModel.views.length; ++i) {
            if (this.viewsModel.views[i].tabUId == tabUId) {
                return this.viewsModel.views[i];
            }
            if (this.viewsModel.views[i].tabs) {
                for (let j = 0; j < this.viewsModel.views[i].tabs.length; ++j) {
                    if (this.viewsModel.views[i].tabs[j].tabUId == tabUId) {
                        return this.viewsModel.views[i];
                    }
                }
            }
        }
        return undefined;
    }

    public enableEditMode(view: CardsModel) {
        if (this.copiedTab) {
            return;
        }
        for (let view of this.viewsModel.views) {
            view.mode = ViewMode.normal;
        }
        view.mode = ViewMode.edit;
    }

    public async duplicateView(view: CardsModel) {
        try {
            this.loading = true;

            let views = await this.dataService.post<ViewsModel>('processing/duplicateView', {
                viewId: view.viewId,
            });
            this.loadViews(views, undefined);
        } finally {
            this.loading = false;
        }
    }

    //"Duplicate" of the Project overview node: a new view with the project's single Outcome card as a fresh start, shown like a duplicated view
    public async duplicateProjectOverview() {
        try {
            this.loading = true;

            let views = await this.dataService.post<ViewsModel>('processing/duplicateProjectOverview', {
                projectId: this.projectId,
            });
            this.loadViews(views, undefined);
        } finally {
            this.loading = false;
        }
    }

    public deleteView(view: CardsModel) {
        this.viewsModel.views = this.viewsModel.views.filter(v => v.viewId != view.viewId);
        if (view.isSelected) {
            this.selectView(this.viewsModel.views[this.viewsModel.views.length - 1]);
        }
        this.dataService.post<ViewsModel>('processing/deleteView', {
            viewId: view.viewId,
        });
    }

    public onSelectTabForCopying(event: { view: CardsModel; tab: Tab | undefined }) {
        this.selectTabForCopying(event.view, event.tab);
    }

    public onCancelCopyMode() {
        this.unselectTabForCopying();
    }

    public async selectTabForCopying(view: CardsModel, tab: Tab) {
        if (this.copiedTab && this.copiedTab.viewId == view.viewId && this.copiedTab.tabId == tab.id) {
            this.unselectTabForCopying();
            return;
        }
        let tabId = tab ? tab.id : undefined;
        this.copiedTab = {
            viewId: view.viewId,
            tabId: tabId,
        };
        let compatibleTabs = await this.dataService.get<TabAddress[]>(`processing/GetCompatibleTabsForCopying?viewId=${view.viewId}&tabId=${tabId}`);
        this.markCompatibleTabs(compatibleTabs);
    }

    private unselectTabForCopying() {
        this.copiedTab = undefined;
        for (let view of this.viewsModel.views) {
            view.mainTabIsMarkedCompatible = false;
            for (let tab of view.tabs) {
                tab.isMarkedCompatible = false;
            }
        }
    }

    private markCompatibleTabs(tabAddresses: TabAddress[]) {
        function tabIsCompatible(viewId: string, tabId: string, tabAddresses: TabAddress[]) {
            return !!tabAddresses.find(ta => ta.viewId == viewId && ta.tabId == tabId);
        }
        for (let view of this.viewsModel.views) {
            view.mainTabIsMarkedCompatible = tabIsCompatible(view.viewId, null, tabAddresses);
            for (let tab of view.tabs) {
                tab.isMarkedCompatible = tabIsCompatible(view.viewId, tab.id, tabAddresses);
            }
        }
        this.selectedTabIsCompatible = tabIsCompatible(this.cardsModel.viewId, this.cardsModel.selectedTabId, tabAddresses);
    }

    public async pasteCopiedTab(cardIx: number) {
        try {
            this.loading = true;
            const currentTabUId = this.getCurrentTabUId();
            const oldCards = this.varianceModel?.cards || [];
            const updatedTargetView = await this.dataService.post<CardsModel>('processing/pasteCopiedTab', {
                sourceViewId: this.copiedTab.viewId,
                sourceTabId: this.copiedTab.tabId,
                targetViewId: this.cardsModel.viewId,
                targetTabId: this.cardsModel.selectedTabId,
                targetCardIx: cardIx,
            });

            CardHelper.highlightNewlyAddedCards(oldCards, updatedTargetView.variance?.cards);

            this.unselectTabForCopying();
            this.loadModel(updatedTargetView, currentTabUId);
        } finally {
            this.loading = false;
        }
    }

    private getCurrentTabUId() {
        if (!this.cardsModel) {
            return undefined;
        }
        return this.cardsModel.selectedTabId ? this.getSelectedTab()?.tabUId : this.cardsModel.tabUId;
    }

    public turnOnMultiselect(mode: CardsMultiSelectMode): void {
        this.multiSelectMode = mode;
    }

    public turnOffMultiSelect() {
        this.multiSelectMode = CardsMultiSelectMode.None;
        if (this.varianceModel && this.varianceModel.cards) {
            for (let card of this.varianceModel.cards) {
                card.isMultiSelected = false;
            }
        }
    }

    public async applyBulkOperation() {
        let indexes = [];
        for (let i = 0; i < this.varianceModel.cards.length; ++i) {
            if (this.varianceModel.cards[i].isMultiSelected) {
                indexes.push(i);
            }
        }
        switch(this.multiSelectMode) {
            case CardsMultiSelectMode.None:
                break;
            case CardsMultiSelectMode.Undecided:
                break;
            case CardsMultiSelectMode.AddToParent:
                await this.addCardsToParent(indexes);
                break;
            case CardsMultiSelectMode.Remove:
                await this.removeCards(indexes);
                break;
            case CardsMultiSelectMode.RequestStory:
                await this.loadStory(this.getSelectedTabUId(), indexes);
                break;
        }
        this.turnOffMultiSelect();
    }

    public noCardsDownloadLinkClick() {
        this.notifier.showInfo('No cards in scope');
    }

    private loadStories() {
        for (let i = 0; i < this.viewsModel.views.length; ++i) {
            this.loadStory(this.viewsModel.views[i].tabUId);
            if (this.viewsModel.views[i].tabs){
                for (let j = 0; j < this.viewsModel.views[i].tabs.length; ++j) {
                    this.loadStory(this.viewsModel.views[i].tabs[j].tabUId);
                }
            }
        }
    }

    private getStoryContainer(view: CardsModel, tab: Tab | undefined) : [IHasStory, boolean] {
        let storyContainer: IHasStory;
        let isSelected: boolean;
        if (tab) {
            if (this.cardsModel && this.cardsModel.viewId == view.viewId && this.cardsModel.selectedTabId == tab.id) {
                isSelected = true;
            }
            storyContainer = tab;
        } else {
            if (this.cardsModel && this.cardsModel.viewId == view.viewId && this.cardsModel.selectedTabId === undefined) {
                isSelected = true;
            }
            storyContainer = view;
        }
        return [ storyContainer, isSelected];
    }

    private loadStory(tabUId: string, ixs: number[] | undefined = undefined) {
        let [ view, tab ] = CardHelper.findByTabUId(this.viewsModel.views, tabUId);
        if (!view ) {
            console.error("Couldn't find a view or a tab with tabUId " + tabUId);
            return;
        }

        let [ storyContainer, isSelected ] = this.getStoryContainer(view, tab);
        storyContainer.loadingStory = true;
        if (isSelected) {
             this.loadingStory = true;
        }

        let tz = this.getTimeZoneOffset();
        let url: string;
        if (ixs) {  //manually called
            url = `processing/generateStory/${view.viewId}?ixs=${ixs.join(',')}&tz=${tz}`;
            if (tab) {
                url += `&tabId=${tab.id}`;
            }
        } else {
            url = `processing/getStory/${this.projectId}?tabUId=${tabUId}&tz=${tz}`;
        }
        //the initial generation is long-polled in slices (see pollStory); the manual "update" call returns the new part directly
        const request = ixs ? this.dataService.get<any>(url) : this.pollStory(this.dataService, url).then(story => ({ story }));
        request.then(res => {
            [ storyContainer, isSelected ] = this.getStoryContainer(view, tab); //search again, cause another tab could've been selected meanwhile
            let newStory: string;
            if (ixs) {
                if (isSelected) {
                    newStory = this.story ? res.story + '<p><br/></p>' + this.story : res.story;
                } else {
                    newStory = storyContainer.story ? res.story + '<p><br/></p>' + storyContainer.story : res.story;
                }
            } else {
                newStory = res.story;
            }

            storyContainer.story = newStory;
            storyContainer.loadingStory = false;
            if (isSelected) {
                this.story = newStory;
                this.loadingStory = false;
                this.unfocusQuill();    //will be sent to the server on blur
            }
        }).catch(err => {
            console.error('Story loading error:');
            console.error(err);
            [ storyContainer, isSelected ] = this.getStoryContainer(view, tab);    //the poll is over: the overlay must not stay
            storyContainer.loadingStory = false;
            if (isSelected) {
                this.loadingStory = false;
            }
        });
    }


    private isInternalNgRoute(url: string): boolean {
        const route = url.split('?')[0].substring(1);
        return this.router.config.some(r => route === r.path);
    }

    public quillBlur($event) {
        if (this.story != this.savedStory) {
            this.saveStory();
        }
    }

    public quillClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        let classes = target.getAttribute('class');
        if (target.tagName === 'A' && target.hasAttribute('href') && classes == 'ql-preview') {
            event.preventDefault();

            const href = target.getAttribute('href');
            console.log('href=' + href);

            const url = decodeURIComponent(href);
            console.log('url=' + url);

            if (this.isInternalNgRoute(url)) {
                this.router.navigateByUrl(CardHelper.createStoryLinkUrlTree(this.router, href, this.projectId));
            } else {
                window.open(url, '_blank');
            }
        }

        //console.log('WYSIWYG story: ' + this.story);
    }

    private saveStory() {
        let tabUId: string;
        let selectedTab = this.getSelectedTab();
        if (selectedTab) {
            tabUId = selectedTab.tabUId;
            selectedTab.story = this.story;
        } else {
            tabUId = this.cardsModel.tabUId;
            this.cardsModel.story = this.story;
        }
        this.savedStory = this.story;
        this.dataService.post('processing/saveStory', {
            projectId: this.projectId,
            tabUId: tabUId,
            story: this.story,
        });
    }

    private getSelectedTabUId(): string {
        let selectedTab = this.getSelectedTab();
        if (selectedTab) {
            return selectedTab.tabUId;
        } else {
            return this.cardsModel.tabUId;
        }
    }

    public selectFcst(val: boolean) {
        this.showFcst = val;
        if (this.cardsModel.selectedTabId) {
            this.selectTabById(this.cardsModel.viewId, this.cardsModel.selectedTabId);
        } else {
            this.selectMainTab(this.cardsModel);
        }
    }

    public onTabChanged(index: number): void {
        this.selectedGuiTabIndex = index;

        // Reflect the selected GUI tab in the URL so it can be deep-linked / bookmarked.
        if (this.activatedRoute.snapshot.queryParams['guiTab'] != index) {
            this.router.navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: { guiTab: index },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });
        }

        if (this.cardsModel) {
            if (this.cardsModel.selectedTabId) {
                const selectedTab = this.getSelectedTab();
                if (selectedTab) {
                    selectedTab.selectedGuiTabIndex = index;
                }
            } else {
                this.cardsModel.selectedGuiTabIndex = index;
            }
        }

        this.ensureGuiTabDataLoaded(index);
    }

    private ensureGuiTabDataLoaded(index: number): void {
        // While the initial processing runs there are no cards yet (loadViews returns before it sets cardsModel)
        if (!this.cardsModel) {
            return;
        }

        // If Related tab is selected and hasn't been loaded yet, load it
        const relatedTabIndex = 4;
        const selectedTab = this.getSelectedTab();
        if (index === relatedTabIndex && selectedTab && !selectedTab.relatedCardsModel) {
            this.loadRelatedCardsData();
        }

        // If Trend tab is selected and hasn't been loaded yet, load it
        const trendTabIndex = 3;
        if (index === trendTabIndex && !this.getBenchmarksModel()) {
            this.loadBenchmarksModelData();
        }
    }

    private async loadRelatedCardsData(): Promise<void> {
        const selectedTab = this.getSelectedTab();
        if (!this.cardsModel?.selectedTabId || !selectedTab) {
            return;
        }

        try {
            const cardsCount = 4;
            selectedTab.relatedCardsModel = await this.projectService.getConfidence(
                this.cardsModel.viewId,
                selectedTab.parentTabId,
                selectedTab.cardIndexInParentTab,
                cardsCount,
                null
            );
            console.log('Confidence data loaded for Related tab:', selectedTab.relatedCardsModel);
        } catch (error) {
            console.error('Error loading confidence data for Related tab:', error);
        }
    }

    public getSelectedTabRelatedCardsModel() {
        const selectedTab = this.getSelectedTab();
        return selectedTab?.relatedCardsModel;
    }

    private async loadBenchmarksModelData(formCompIx?: number): Promise<void> {
        if (!this.cardsModel?.viewId) {
            return;
        }

        const selectedTab = this.getSelectedTab();
        const cacheContainer = selectedTab || this.cardsModel;

        // Only cache for URL without formCompIx. A default load also leaves a load on its way alone, e.g. the
        // formula component of a story's trend link: coming back later, the default model would replace it
        if (formCompIx === undefined && (cacheContainer.benchmarksModel || this.benchmarksModelRequest?.container === cacheContainer)) {
            console.log('loadBenchmarksModelData: Using cached default data');
            return;
        }

        const request = { container: cacheContainer };
        this.benchmarksModelRequest = request;
        try {
            let url: string;
            if (selectedTab) {
                url = `benchmarks?viewId=${this.cardsModel.viewId}&tabId=${selectedTab.parentTabId}&cardIndex=${selectedTab.cardIndexInParentTab}`;
            } else {
                url = `benchmarks?viewId=${this.cardsModel.viewId}`;
            }
            if (formCompIx !== undefined) {
                url += `&formCompIx=${formCompIx}`;
            }
            const res = await this.dataService.get<BenchmarksModel>(url);

            // A later load (another formula component's, another tab's) supersedes this one
            if (this.benchmarksModelRequest === request) {
                cacheContainer.benchmarksModel = res;
            }
        } catch (error) {
            console.error('Error loading BenchmarksModel for Trend tab:', error);
        } finally {
            if (this.benchmarksModelRequest === request) {
                this.benchmarksModelRequest = undefined;
            }
        }
    }

    public onFormCompChanged(formCompIx: number): void {
        this.loadBenchmarksModelData(formCompIx);
    }

    public async onFcstSaved(event: { formCompIx: number }): Promise<void> {
        this.loading = true;
        const currentGuiTabIndex = this.selectedGuiTabIndex;

        await this.reloadCurrentView();
        this.selectedGuiTabIndex = currentGuiTabIndex;

        await this.cardsLoadingService.loadMainFcstWalkModel(this.projectId, true);
        this.mainFcstWalkModel = this.cardsLoadingService.mainFcstWalkModel;

        this.loadBenchmarksModelData(event.formCompIx);
        this.loading = false;
    }

    public getBenchmarksModel(): BenchmarksModel {
        let selectedTab = this.getSelectedTab();
        if (selectedTab) {
            return selectedTab.benchmarksModel;
        }
        return  this.cardsModel.benchmarksModel;
    }

    public getDisplayedOutcomeBeforeOutOfScope(): number {
        //console.log('getDisplayedOutcomeBeforeOutOfScope():');
        //console.log('this.varianceModel.outComeBeforeOutOfScope', this.varianceModel.outComeBeforeOutOfScope);
        //console.log('this.toSeqBeforeOutOfScope', this.toSeqBeforeOutOfScope);
        if (this.toTotBeforeOutOfScope === undefined) {
            return this.varianceModel.outComeBeforeOutOfScope;
        }
        return this.seqMode ? this.toSeqBeforeOutOfScope : this.toTotBeforeOutOfScope;
    }

    public getSeqModeSecondaryValue(): number {
        return this.cardsModel.selectedTabId
            ? this.varianceModel.newTotalOutcome
            : this.varianceModel.outComeBeforeOutOfScope;
    }

    public getSeqModeSecondaryValueAfterOutOfScope(): number {
        return this.getOutcomeBasedOnOutOfScopeAndTab();
    }

    public getDisplayedOutcomeAfterOutOfScope(): number {
        if (this.toTotBeforeOutOfScope === undefined) {
            return this.getOutcomeBasedOnOutOfScopeAndTab();
        }

        if (this.showOutOfScope) {
            return this.seqMode ? this.toSeqAfterOutOfScope : this.toTotAfterOutOfScope;
        } else {
            return this.seqMode ? this.toSeqBeforeOutOfScope : this.toTotBeforeOutOfScope;
        }
    }

    private getOutcomeBasedOnOutOfScopeAndTab(): number {
        const scopeMatchesTab = this.showOutOfScope !== !!this.cardsModel.selectedTabId;

        if (scopeMatchesTab) {
            return this.varianceModel.outComeBeforeOutOfScope;
        }

        return this.showOutOfScope
            ? this.varianceModel.outcomeAfterOutOfScope
            : this.varianceModel.outComeBeforeOutOfScope;
    }

    private getTimeZoneOffset() {
        return -new Date().getTimezoneOffset();
    }

    private saveSelectedViewId(viewId: string): void {
        localStorage.setItem(CardHelper.getSelectedViewKey(this.projectId), viewId);
    }

    private getSavedSelectedViewId() {
        return localStorage.getItem(CardHelper.getSelectedViewKey(this.projectId));
    }

    public async deleteProject() {
        await this.projectService.deleteProject(this.projectId);
        this.router.navigateByUrl('/projects');
    }

    public async showSettingsDialog() {
        try {
            this.loading = true;
            let aggregationSettings = await this.aggregationSevice.getAggregationSettings(this.projectId);
            const dialogData: SettingsModalData = {
                viewId: this.cardsModel?.viewId,
                tabId: this.cardsModel?.selectedTabId,
                outcomeName: this.cardsModel?.outcomeName,
                algoSettings: {
                    selectedAlgorithm: this.selectedAlgorithm,
                    minDriverValue: this.minDriverValue,
                    minDriverValueIsAuto: this.minDriverValueIsAuto,
                },
                driverTypeFilter: this.driverTypeFilter,
                aggregationSettings: UIAggregationSettings.fromCore(aggregationSettings),
            };

            const dialogRef = this.dialog.open(SettingsModalComponent, {
                data: dialogData,
                autoFocus: false,
            });
            dialogRef.afterClosed().subscribe(async result => {
                if (result) {
                    try {
                        this.loading = true;
                        let settings = result as SettingsModalData;
                        let aggregationMethods = settings.aggregationSettings.formComps.map(fc =>
                            UIAggregationMethod.toCore(fc.aggregationMethod)
                        );
                        let model = await this.dataService.post<CardsModel>('Processing/SaveSettings', {
                            viewId: settings.viewId,
                            tabId: settings.tabId,
                            algorithm: settings.algoSettings.selectedAlgorithm,
                            minDriverValue: settings.algoSettings.minDriverValue,
                            minDriverValueIsAuto: settings.algoSettings.minDriverValueIsAuto,
                            driverTypeFilter: settings.driverTypeFilter,
                            formulaComponentAggregationMethods: aggregationMethods,
                        });
                        this.loadModel(model);
                    } finally {
                        this.loading = false;
                    }
                }
            });
        } finally {
            this.loading = false;
        }
    }

    showEditFilterExpressionDialog() {
        const dialogRef = this.dialog.open(EditFilterExpressionModalComponent, {
            data: {
                dimensionsFilter: JSON.parse(JSON.stringify(this.cardsModel.dimensionsFilter)),
                legend: this.cardsModel.legend,
                projectId: this.projectId,
                viewId: this.cardsModel.viewId,
                selectedTabId: this.cardsModel.selectedTabId,
                tabDimensionsFilter: this.cardsModel.selectedTabId ? CardHelper.cloneObject(this.tabDimensionsFilter) : null,
            },
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result && result.success && result.cardsModel) {
                // Apply the updated model
                this.loadModel(result.cardsModel);
            }
        });
    }

    showRelatedCardsDialog(cardIx: number) {
        let dialogParams = {
            projectId: this.projectId,
            viewId: this.cardsModel.viewId,
            tabId: this.cardsModel.selectedTabId,
            cardIx: cardIx,
            cardsCount: 4,
            seqMode: this.seqMode,
        };

        const dialogRef = this.dialog.open(RelatedCardsModalComponent, {
            data: dialogParams,
            width: '90vw',
            maxWidth: '1400px',
            height: '90vh',
        });
        dialogRef.componentInstance.seqModeChange.subscribe(seqMode => this.setSeqMode(seqMode));

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.shouldReload) {
                this.reloadCurrentView();
            } else if (result && result.shouldExplode) {
                this.explodeCard(result.cardIx, null, 4); // Explode and navigate to Related tab
            }
        });
    }

    /** formCompIx: the formula component whose trend to show (-1: the outcome), by default the card's own driver */
    showTrendForCard(cardIx: number, formCompIx?: number) {
        // Find if a tab already exists for this card
        const existingTab = this.cardsModel.tabs?.find(t =>
            t.parentTabId === this.cardsModel.selectedTabId &&
            t.cardIndexInParentTab === cardIx
        );

        if (existingTab && existingTab.tabUId) {
            // Tab exists, navigate to it with Trend tab active
            this.router.navigate(['/cards'], {
                queryParams: { projectId: this.projectId, tabuid: existingTab.tabUId, formCompIx: formCompIx },
                state: { guiTab: 3 } // Trend tab index
            });
        } else {
            // No tab exists, open modal
            let dialogParams = {
                projectId: this.projectId,
                viewId: this.cardsModel.viewId,
                tabId: this.cardsModel.selectedTabId,
                cardIx: cardIx,
                formCompIx: formCompIx,
                seqMode: this.seqMode,
            };

            const dialogRef = this.dialog.open(TrendModalComponent, {
                data: dialogParams,
                width: '90vw',
                maxWidth: '1400px',
                height: '90vh',
            });
            dialogRef.componentInstance.seqModeChange.subscribe(seqMode => this.setSeqMode(seqMode));

            dialogRef.afterClosed().subscribe(async result => {
                if (result && result.shouldReload) {
                    await this.onFcstSaved({ formCompIx: result.formCompIx });
                } else if (result && result.shouldExplode) {
                    this.explodeCard(result.cardIx, null, 3, result.formCompIx); // Explode and navigate to Trend tab, on the modal's variable
                }
            });
        }
    }

    public gotoSeqWalk(){
        this.selectedGuiTabIndex = 0;
        this.setSeqMode(true);
    }

    public gotoTotWalk(){
        this.selectedGuiTabIndex = 0;
        this.setSeqMode(false);
    }
}
